/**
 * @documentation true
 * @sectionName Merging data
 */
import * as deepmerge from 'deepmerge';
import { pipe, sort, unique } from './index';
/**
 * @internal
 */
function _deepEquals(arrayCb) {
    function deep(a, b) {
        return (a === b ||
            (typeof a === 'object' &&
                typeof b === 'object' &&
                a != null &&
                b != null &&
                (Array.isArray(a)
                    ? Array.isArray(b) && arrayCb(a, b, deep)
                    : Object.keys(a).length === Object.keys(b).length &&
                        Object.entries(a).every(([k, v]) => deep(v, b[k])))));
    }
    return deep;
}
/**
 * Checks if two items are equal
 */
export const deepEquals = _deepEquals((a, b, deepCb) => a.length === b.length && a.every((v, i) => deepCb(v, b[i])));
/**
 * Checks If two arrays are equal ignoring order
 */
export const deepItemsEqual = _deepEquals((a, b, deepCb) => a.length === b.length &&
    ((aa, bb) => aa.every((v, i) => deepCb(v, bb[i])))(sort()(a), sort()(b)));
export function itemsEqual(a, b) {
    return (a.length === b.length && ((aa, bb) => aa.every((v, i) => v === bb[i]))(sort()(a), sort()(b)));
}
export function isPlainObject(value) {
    return value != null && {}.toString.call(value) === '[object Object]';
}
/**
 * Merge same type object multiple times, and returns final full object
 * @remarks
 * NOTE - Function will merge arrays
 * @param base - Full object. From this object typescript will infer next partial object
 * @param obj - List of partial base objects
 *
 * @example
 * ```typescript
 * interface A {
 *  a: string
 *  b?: string
 *  c?: string[]
 * }
 *
 * const bar: A = {
 *  a: 'string1'
 * }
 *
 * const listOfATypeObjects: A = [
 *      { b: 'string2' },
 *      { c: ["s1"] },
 *      { c: ["s2"] },
 *      { c: ["s3"]}
 * ]
 *
 * const final = repeatMerge(bar, ...listOfATypeObjects)
 * @Output: {
 *      a:"string1",
 *      b: "string2",
 *      c: ["s1","s2","s3"]
 * }
 * ```
 * @public
 */
export function repeatMerge(base, ...obj) {
    return Object.assign({}, base, deepmerge.all([base, ...obj], {
        arrayMerge: (target, source) => {
            return pipe([...target, ...source], unique());
        },
    }));
}
/**
 * Deep merger merges objects and owerwrites array
 */
export function deepMerge(a, b) {
    return isPlainObject(a) && isPlainObject(b)
        ? Object.assign(Object.assign({}, a), ...Object.entries(b).map(([k, v]) => ({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            [k]: k in a ? deepMerge(a[k], v) : v,
        })))
        : b;
}
// tslint:disable:no-if-statement ban-types readonly-array typedef
